// REST client — fetch-wrapper pattern from workday-poc data.jsx, as ESM.
//
// Base path resolves from BenInsights runtime config (AppConfig).
// In local dev it currently defaults to "http://localhost:8001/api" (see AppConfig.WORKDAY_ACTIVATION_API_URL).
// Auth wiring can be introduced in a follow-up story.
import { AppConfig } from 'AppConfig';
import LocalStore from 'utils/LocalStore';

const API_BASE = AppConfig.WORKDAY_ACTIVATION_API_URL;

async function request(path, options) {
  const response = await fetch(API_BASE + path, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${LocalStore.getAccessToken()}`
    },
    credentials: "include",
    ...options,
  });
  if (!response.ok) {
    let detail = await response.text();
    try {
      detail = JSON.parse(detail).detail || detail;
    } catch {
      /* keep raw text */
    }
    const error = new Error(`${options?.method || "GET"} ${path} failed (${response.status}): ${detail}`);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }
  return response.json();
}

export const getHealth = () => request("/health");
export const getAuthContext = () => request("/auth/context");
export async function getDevelopmentRoles() {
  try {
    return await request("/dev/auth/roles");
  } catch (error) {
    if (error.status === 404) return null;
    throw error;
  }
}
export const setDevelopmentRoles = (roles) =>
  request("/dev/auth/roles", { method: "PUT", body: JSON.stringify({ roles }) });
export const resetDevelopmentRoles = () => request("/dev/auth/roles", { method: "DELETE" });
export async function getDevelopmentDataMode() {
  try {
    return await request("/dev/data-mode");
  } catch (error) {
    if (error.status === 404) return null;
    throw error;
  }
}
export const setDevelopmentDataMode = (mode) =>
  request("/dev/data-mode", { method: "PUT", body: JSON.stringify({ mode }) });
export const getTenantContext = () => request("/tenant/context");
export const getEnums = () => request("/meta/enums");
export const getPrograms = () => request("/programs");
export const getEnrollmentSummary = () => request("/enrollment/summary");
export const getRuns = (programDomain) =>
  request("/runs" + (programDomain ? `?programDomain=${programDomain}` : ""));
export const getRunQuality = (runId) => request(`/runs/${runId}/quality`);
export const getOpportunitiesSummary = (runMonth) =>
  request("/opportunities/summary" + (runMonth ? `?runMonth=${runMonth}` : ""));
export const getBreakdown = (programDomain, dimension, runMonth) =>
  request(
    `/opportunities/breakdown?programDomain=${programDomain}&dimension=${dimension}` +
      (runMonth ? `&runMonth=${runMonth}` : "")
  );
export const getFunnel = (programDomain, runMonth) =>
  request(`/opportunities/funnel?programDomain=${programDomain}` + (runMonth ? `&runMonth=${runMonth}` : ""));
export const getTrends = (programDomain) => request(`/opportunities/trends?programDomain=${programDomain}`);
export const getOpportunityMonitoring = () => request("/monitoring/opportunity-trends");
export const estimateCohort = (body) =>
  request("/cohorts/estimate", { method: "POST", body: JSON.stringify(body) });
export const getTemplates = (programDomain, channel) => {
  const params = new URLSearchParams();
  if (programDomain) params.set("programDomain", programDomain);
  if (channel) params.set("channel", channel);
  const qs = params.toString();
  return request("/templates" + (qs ? `?${qs}` : ""));
};
export const getSensitivityPolicies = () => request("/policies/sensitivity");
export const getGovernancePolicy = () => request("/policies/governance");
export const getCampaigns = (status) => request("/campaigns" + (status ? `?status=${status}` : ""));
export const getCampaign = (id) => request(`/campaigns/${id}`);
export const createCampaign = (body) => request("/campaigns", { method: "POST", body: JSON.stringify(body) });
export const updateCampaign = (id, body) =>
  request(`/campaigns/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const cloneCampaign = (id, body = {}) =>
  request(`/campaigns/${id}/clone`, { method: "POST", body: JSON.stringify(body) });
export const runGovernanceCheck = (id) =>
  request(`/campaigns/${id}/governance-check`, { method: "POST", body: "{}" });
export const transitionCampaign = (id, action, note = "") =>
  request(`/campaigns/${id}/transition`, {
    method: "POST",
    body: JSON.stringify({ action, note }),
  });
export const getCampaignHistory = () => request("/campaign-history");
export const searchRestrictedOpportunities = (body) =>
  request("/restricted/opportunities/search", {
    method: "POST",
    body: JSON.stringify(body),
  });
